import { httpClient } from '@/lib/api';

import type {
  CandidateMergeResult,
  CvImport,
  CvUploadResult,
  CvImportFilters,
  CvImportSummary,
  CvContactCompletion,
} from '../types';

/**
 * Délai d'envoi des fichiers.
 *
 * Plus long que les 20 s par défaut du client : la requête ne fait que déposer
 * les fichiers — l'analyse, elle, est asynchrone — mais cinq CV de 10 Mo sur
 * une connexion montante modeste prennent facilement plus de vingt secondes.
 */
const UPLOAD_TIMEOUT_MS = 120_000;
export interface CvUpdateProposal {
  candidateId: string;
  updatedAt: string;
  appliedAt: string | null;
  skillsComparison?: { current: string[]; detected: { label: string; alreadyPresent: boolean }[] };
  changes: {
    key: string;
    label: string;
    before: string;
    after: string;
    section?: string;
    operation?: 'ADD' | 'UPDATE';
    title?: string;
    details?: {
      label: string;
      before: string;
      after: string;
      changed: boolean;
      selectionKey?: string;
    }[];
  }[];
}

export const cvImportsApi = {
  completionFile: (id: string) => httpClient.getBlob(`/cv-imports/${id}/contact-completion/file`),
  contactCompletion: (id: string) =>
    httpClient.get<CvContactCompletion>(`/cv-imports/${id}/contact-completion`),
  completeContact: (id: string, contacts: { email: string; phone: string }) =>
    httpClient.post<{ candidateId: string }>(`/cv-imports/${id}/contact-completion`, contacts),
  pendingComparison: (candidateId: string) =>
    httpClient.get<{ id: string } | null>(`/cv-imports/candidate/${candidateId}/comparison`),
  updateCandidateCv: (id: string, file: File, mode: 'REPLACE' | 'ANALYZE') => {
    const form = new FormData();
    form.append('files', file);
    form.append('mode', mode);
    return httpClient.post<CvUploadResult>(`/cv-imports/candidate/${id}`, form, {
      timeoutMs: UPLOAD_TIMEOUT_MS,
    });
  },
  proposal: (id: string) => httpClient.get<CvUpdateProposal>(`/cv-imports/${id}/proposal`),
  applyProposal: (id: string, selected: string[], updatedAt: string) =>
    httpClient.post<{ candidateId: string }>(`/cv-imports/${id}/apply`, { selected, updatedAt }),
  list: (filters: CvImportFilters) =>
    httpClient.getPaginated<CvImport>('/cv-imports', { query: { ...filters } }),
  summary: () => httpClient.get<CvImportSummary>('/cv-imports/summary'),
  markRead: (ids: string[]) =>
    httpClient.patch<{ count: number }>('/cv-imports/notifications/read', { ids }),
  /**
   * `POST /cv-imports` — dépose un à cinq CV.
   *
   * Le nom de champ `files` est imposé par le serveur (`multer.array('files')`).
   * Aucun `Content-Type` n'est posé : le navigateur doit générer lui-même la
   * frontière multipart, et la fixer à la main casserait l'envoi.
   */
  upload: (files: File[]): Promise<CvUploadResult> => {
    const form = new FormData();
    for (const file of files) form.append('files', file);

    return httpClient.post<CvUploadResult>('/cv-imports', form, {
      timeoutMs: UPLOAD_TIMEOUT_MS,
    });
  },

  /** `GET /cv-imports/:id` — avancement d'un dépôt. */
  getById: (id: string): Promise<CvImport> => httpClient.get<CvImport>(`/cv-imports/${id}`),

  /**
   * `POST /candidates/:id/merge` — fusionne un doublon dans la fiche `targetId`,
   * qui reste la fiche de référence.
   */
  merge: (targetId: string, duplicateId: string): Promise<CandidateMergeResult> =>
    httpClient.post<CandidateMergeResult>(`/candidates/${targetId}/merge`, { duplicateId }),

  /** `DELETE /candidates/:id/duplicate` — « ce n'est pas un doublon ». */
  dismissDuplicate: (id: string): Promise<unknown> =>
    httpClient.delete(`/candidates/${id}/duplicate`),
};
